import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import type { AttributeGroup, Attribute } from '@/pages/AttributeManagement';

interface GroupManagerProps {
  groups: AttributeGroup[];
  onGroupsChange: (groups: AttributeGroup[]) => void;
  activeGroupId: string;
  onActiveGroupChange: (groupId: string) => void;
  onAttributeDropOnGroup: (attributes: Attribute[]) => void;
  attributes: Attribute[];
}

const GroupManager = ({ 
  groups, 
  onGroupsChange, 
  activeGroupId, 
  onActiveGroupChange,
  onAttributeDropOnGroup,
  attributes
}: GroupManagerProps) => {
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);

  const sortedGroups = [...groups].sort((a, b) => a.name.localeCompare(b.name));


  const handleEditStart = (group: AttributeGroup) => {
    setEditingGroupId(group.id);
    setEditingName(group.name);
  };

  const handleEditSave = () => {
    if (!editingName.trim()) {
      toast.error('Group name cannot be empty');
      return;
    }

    if (groups.some(g => g.id !== editingGroupId && g.name.toLowerCase() === editingName.toLowerCase())) {
      toast.error('Group name already exists');
      return;
    }

    const updatedGroups = groups.map(g => 
      g.id === editingGroupId 
        ? { ...g, name: editingName.trim() }
        : g
    );

    onGroupsChange(updatedGroups);
    setEditingGroupId(null);
    setEditingName('');
    
    toast.success('Group name updated successfully');
  };

  const handleEditCancel = () => {
    setEditingGroupId(null);
    setEditingName('');
  };

  const handleDelete = () => {
    if (!deleteConfirmId) return;

    const updatedGroups = groups.filter(g => g.id !== deleteConfirmId);
    onGroupsChange(updatedGroups);
    setDeleteConfirmId(null);
    
    toast.success('Group deleted successfully');
  };

  const handleDragOver = (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverGroupId(groupId);
  };

  const handleDragLeave = () => {
    setDragOverGroupId(null);
  };

  const handleDrop = (e: React.DragEvent, targetGroupId: string) => {
    e.preventDefault();
    setDragOverGroupId(null);
    
    const attributeId = e.dataTransfer.getData('text/plain');
    if (!attributeId) return;

    const attributeToMove = attributes.find(attr => attr.id === attributeId);
    if (!attributeToMove || attributeToMove.groupId === targetGroupId) return;

    // Move attribute to new group and update order
    const targetGroupAttributes = attributes.filter(attr => attr.groupId === targetGroupId);
    const updatedAttributes = attributes.map(attr => {
      if (attr.id === attributeId) {
        return { ...attr, groupId: targetGroupId, order: targetGroupAttributes.length + 1 };
      }
      return attr;
    });

    onAttributeDropOnGroup(updatedAttributes);
    toast.success('Attribute moved to new group');
  };

  return (
    <div className="space-y-6">
      {/* Groups Tabs */}
      <Tabs value={activeGroupId} onValueChange={onActiveGroupChange}>
        <TabsList className="flex flex-wrap gap-2 h-auto p-3 bg-muted/50 justify-start">
          {sortedGroups.map((group) => (
            <div key={group.id} className="flex items-center gap-1">
              <TabsTrigger 
                value={group.id}
                className={`flex items-center gap-2 text-xl px-8 py-4 min-h-[4rem] font-bold ${
                  dragOverGroupId === group.id ? 'bg-accent/50 border-2 border-primary' : ''
                } ${
                  activeGroupId === group.id ? 'text-left' : 'text-left'
                }`}
                onDragOver={(e) => handleDragOver(e, group.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, group.id)}
              >
                {editingGroupId === group.id ? (
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') handleEditSave();
                        if (e.key === 'Escape') handleEditCancel();
                      }}
                      className="h-8 text-sm min-w-0 w-32"
                      autoFocus
                    />
                    <Button 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditSave();
                      }}
                    >
                      <Save size={14} />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCancel();
                      }}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ) : (
                  <>
                    <span className="font-bold text-left">{group.name}</span>
                    {activeGroupId === group.id && (
                      <div className="flex items-center gap-1 ml-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 hover:bg-accent"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditStart(group);
                          }}
                        >
                          <Edit2 size={12} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(group.id);
                          }}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </TabsTrigger>
            </div>
          ))}
        </TabsList>
      </Tabs>


      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this group? This action cannot be undone.
              All attributes in this group will also be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupManager;